import axios, { AxiosProgressEvent, AxiosResponse, ResponseType as AxiosResponseType } from 'axios';
import TaskPromise from './TaskPromise';
import type { AuthTokenResponse, IHTTPDataService } from './IHTTPDataService';
import { fileUploadErrors } from '../data/labels';
import { getWrap } from '../helpers/listsHelpers';
import { saveAs } from 'file-saver';
import { isArray } from '../helpers/typeHelpers';
import { isObject } from '../helpers/objectHelper';

export abstract class HTTPDataService implements IHTTPDataService {
    abstract baseURL: string;
    private static unauthorizedHandler: (() => void) | undefined;

    static setUnauthorizedHandler = (handler?: () => void) => {
        HTTPDataService.unauthorizedHandler = handler;
    };

    static triggerUnauthorized = () => {
        const handler = HTTPDataService.unauthorizedHandler;
        if (!handler) return;

        try {
            handler();
        } catch (error) {
            console.error('Failed to trigger unauthorized handler.', error);
        }
    };

    static isAuthorizationFailure = (status?: number, data?: any) => {
        const normalizedStatus = typeof status === 'number' ? status : Number(status ?? 0);
        if (normalizedStatus === 401 || normalizedStatus === 403) return true;

        const payload = typeof data === 'string' ? data : data && typeof data === 'object' ? JSON.stringify(data) : '';
        if (!payload) return false;

        const normalized = payload.toLowerCase();
        return /(unauthorized|invalid token|expired token|token expired|token invalid|authentication failure|login_required|invalid_grant|not authenticated|access denied)/.test(normalized);
    };

    abstract getAccessToken(): Promise<string | undefined>;

    toAbsoluteApiUrl(endpoint: string, params?: Record<string, string>) {
        let url = endpoint;
        if (
            url.indexOf("http") !== 0 &&
            url.indexOf("//") !== 0 &&
            url.indexOf("/") !== 0
        ) {
            url = this.baseURL + "/" + url;
        }

        if (!params) return url;
        if (url.indexOf('?') === -1) url += "?";
        url += Object.keys(params).map(key => key + '=' + params[key]).join('&');

        return url;
    }

    promiseTokenCache: Promise<AuthTokenResponse | undefined> | undefined;
    cacheToken: AuthTokenResponse | undefined;
    private async getAuthToken() {
        const token = await this.getAccessToken();
        if (!token) return undefined;
        return { token };
    }

    getAcceptLanguageValue = () => {
        const languages = [this.getPreferredLanguage(), ...navigator.languages];
        let q = 1;
        return languages
            .filter(x => !!x)
            .slice(0, 10)
            .map(l => `${l};q=0.${10 - q++}`)
            .join(",");
    };
    getPreferredLanguage = () => localStorage.getItem("language");
    setPreferredLanguage = (language: string) => localStorage.setItem("language", language);

    private request<ResponseType>(
        method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
        endpoint: string,
        params?: Record<string, string | number | undefined | null>,
        data?: any,
        onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
        onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void,
        responseType?: AxiosResponseType,
        returnRawResponse = false
    ): TaskPromise<ResponseType> {

        const cancelTokenSource = axios.CancelToken.source();
        const promise = new TaskPromise<ResponseType>(async (resolve, reject) => {
            const url = this.toAbsoluteApiUrl(endpoint);

            let attemptCount = 0;
            const maxAttemptCount = 1;
            const call = async () => {
                // The number of times the `call` function has been triggered.
                attemptCount++;

                const handleError = (error: any, reattempt: boolean) => {
                    const response = error.response || error;

                    if (axios.isCancel(error)) {
                        reject('cancellationError');
                        return;
                    }

                    const status = response?.status ?? 0;
                    const statusText = response?.statusText || response?.data?.Message || response?.data?.message || response?.data?.detail || '';

                    if (HTTPDataService.isAuthorizationFailure(status, response?.data)) {
                        HTTPDataService.triggerUnauthorized();
                    }

                    // retry if i get 401 and i have a token 
                    if (!reattempt || !token || status !== 401) {
                        const httpError: HttpError = {
                            status,
                            statusText,
                            data: response?.data,
                            config: response?.config,
                        };
                        reject(httpError);
                    }
                    else call();
                };

                let token: string | undefined = undefined;

                try {
                    token = (await this.getAuthToken())?.token; // force token refresh if this is not the first attempt.
                } catch (err: any) {
                    handleError(err, false); // do not reattempt in this case. The token call has failed.
                    return;
                }

                const headers: Record<string, string> = {
                    // 'Accept-Language': this.getAcceptLanguageValue(),
                    'Accept': '*/*',
                    'Referrer-Policy': 'no-referrer',
                };
                
                if (token)
                    headers["Authorization"] = `Bearer ${token}`;
                
                //console.logurl);
                
                axios({
                    url,
                    method,
                    data,
                    params,
                    timeout: 60000, // 60 seconds timeout for all requests
                    withCredentials: false,
                    cancelToken: cancelTokenSource.token,
                    headers,
                    onUploadProgress,
                    onDownloadProgress,
                    responseType
                })
                    .then((response) => resolve(returnRawResponse ? response : response.data))
                    .catch((err) => handleError(err, attemptCount < maxAttemptCount)); // reattempt only if maxAttemptCount is not reached.

                // // Dummy response for testing
                // const dummyResponse = {
                //     data: {},
                //     status: 200,
                //     statusText: 'OK',
                //     headers: {},
                //     config: {},
                //     onUploadProgress : false,
                //     onDownloadProgress : false
                // };
                // resolve(returnRawResponse ? dummyResponse as any : dummyResponse.data as any);
            };
            await call();
        });

        promise.disposed(() => {
            cancelTokenSource.cancel();
        });
        return promise;
    }

    private objectToArray = <TObject, TArrayItem>(data: TObject | TArrayItem[], t1Key: keyof TObject): TArrayItem[] => {
        if (isArray(data)) return data as TArrayItem[];
        if (isObject(data)) return data?.[t1Key] as TArrayItem[] || [];
        throw new Error('Invalid data format');
    };

    getObjectArray = async <TObject, TArrayItem>(path: string, objectKey: keyof TObject, queryParams?: Record<string, string | number | undefined | null>) => {
        const obj = await this.get<TObject | TArrayItem[]>(path, queryParams);
        return this.objectToArray(obj, objectKey);
    };

    get = <T>(path: string, queryParams?: Record<string, string | number | undefined | null>) => {
        return this.request<T>('GET', path, queryParams);
    };

    post = <T>(path: string, queryParams?: Record<string, string | number | undefined | null>, data?: Record<string, any>, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void, onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
        return this.request<T>('POST', path, queryParams, data, onUploadProgress, onDownloadProgress);
    };

    postStream = <T>(path: string, queryParams?: Record<string, string | number | undefined | null>, data?: Record<string, any>) => {
        return this.request<T>('POST', path, queryParams, data);
    };

    patch = <T>(path: string, queryParams?: Record<string, string | number | undefined | null>, data?: Record<string, any>) => {
        return this.request<T>('PATCH', path, queryParams, data);
    };

    delete = <T>(path: string, queryParams?: Record<string, string | number | undefined | null>, data?: Record<string, any>) => {
        return this.request<T>('DELETE', path, queryParams, data);
    };
    downloadBlob = async (path: string, contentType?: string, method: "GET" | "POST" = "GET", queryParams?: Record<string, string | number | undefined | null>, data?: Record<string, any>): Promise<{ blob: Blob, filename: string; }> => {
        const response = await this.request<AxiosResponse<any, any>>(method, path, queryParams, data, undefined, undefined, "blob", true);

        const blob = new Blob([response.data], {
            type: (contentType || response.headers['content-type'] || 'application/octet-stream') as string,
        });

        // Ensure filename is set correctly, falling back if necessary
        let filename = response.headers['content-disposition']
            ? (response.headers['content-disposition'] as string).split('filename=')[1].replace(/"/g, '') // Extract filename from content-disposition header
            : 'download'; // Fallback filename

        if (filename.includes('/'))
            filename = getWrap(filename.split('/'), -1);

        return { blob, filename };
    };
    download = async (path: string, contentType?: string, downloadFilename?: string, method: "GET" | "POST" = "GET", queryParams?: Record<string, string | number | undefined | null>, data?: Record<string, any>, raiseErrors = false): Promise<boolean> => {
        try {
            const { blob, filename } = await this.downloadBlob(path, contentType, method, queryParams, data);
            saveAs(blob, downloadFilename || filename);
            return true;
        } catch (error) {
            if (raiseErrors) {
                throw error;
            }
            console.error(fileUploadErrors.downloadError.message, error);
            return false;
        }
    };
}


export type HttpError = {
    status: number,
    statusText: string;
    data?: any,
    config?: any;
};